import React, { useContext, useMemo } from 'react';
import { ShopContext } from '../context/ShopContext';
import { TShirtIcon } from './Icons';
import Hero from './Hero';
import ValueProps from './ValueProps';
import ProductCard from './ProductCard';
import VideoShowcase from './VideoShowcase';
import JumpingText from './JumpingText';
import FloatingShopCollage from './FloatingShopCollage';
import './Home.css';

const DEFAULT_BOYS_TEES_POLOS = [
  {
    id: 'db-polo-blend-men-1',
    name: 'Midnight Silk Blended Polo',
    price: 2899,
    category: 'Polos',
    poloType: 'Blended Polo',
    gender: 'Mens',
    stock: 25,
    description: 'Handcrafted luxury breathable silk-blend polo with signature engraved buttons.',
    tag: 'Silk Blend',
    sizes: ['S', 'M', 'L', 'XL'],
    colors: ['Black', 'Navy'],
    imageUrl: '/images/cat-slide-men-polo.webp',
    image: '/images/cat-slide-men-polo.webp',
    images: ['/images/cat-slide-men-polo.webp'],
    rating: 4.9,
    reviews: 80
  },
  {
    id: 'db-tee-acid-wash-men-1',
    name: 'Acid Wash 240 GSM Oversized Tee',
    price: 1199,
    category: 'Oversized T-Shirts',
    gender: 'Mens',
    stock: 45,
    description: 'Heavyweight premium cotton tee with authentic acid-wash texture and dropped shoulders.',
    tag: 'Trending',
    sizes: ['M', 'L', 'XL', 'XXL'],
    colors: ['Washed Gray', 'Vintage Black'],
    imageUrl: '/images/cat-slide-men-classic.webp',
    image: '/images/cat-slide-men-classic.webp',
    images: ['/images/cat-slide-men-classic.webp'],
    rating: 4.8,
    reviews: 36
  }
];

const DEFAULT_GIRLS_TEES_POLOS = [
  {
    id: 'db-polo-blend-women-1',
    name: 'Luxe Modal-Cotton Blended Polo',
    price: 1899,
    category: 'Polos',
    poloType: 'Blended Polo',
    gender: 'Womens',
    stock: 20,
    description: 'Ultra-luxurious Supima cotton & micro-modal blended knit polo with liquid drape.',
    tag: 'Silk Blend',
    sizes: ['S', 'M', 'L'],
    colors: ['Cream', 'Black'],
    imageUrl: '/images/cat-slide-women-classic.webp',
    image: '/images/cat-slide-women-classic.webp',
    images: ['/images/cat-slide-women-classic.webp'],
    rating: 4.9,
    reviews: 31
  },
  {
    id: 'db-tee-oversized-women-1',
    name: 'Luxe Signature Oversized Tee',
    price: 1299,
    category: 'Oversized T-Shirts',
    gender: 'Womens',
    stock: 40,
    description: '240 GSM organic combed cotton relaxed silhouette oversized tee with soft bio-wash finish.',
    tag: 'Daily Essential',
    sizes: ['XS', 'S', 'M', 'L'],
    colors: ['Beige', 'Lavender', 'Off-White'],
    imageUrl: '/images/cat-slide-women-classic.webp',
    image: '/images/cat-slide-women-classic.webp',
    images: ['/images/cat-slide-women-classic.webp'],
    rating: 4.7,
    reviews: 28
  }
];

const DEFAULT_SHIRTS = [
  {
    id: 'db-3',
    name: 'Structured Utility Oxford Shirt',
    price: 1899,
    category: 'Shirts',
    gender: 'Mens',
    stock: 30,
    description: 'Tailored streetwear oxford button-down shirt with dual flap pockets.',
    tag: 'Signature',
    sizes: ['M', 'L', 'XL'],
    colors: ['White', 'Olive', 'Navy'],
    imageUrl: '/images/cat-slide-men-classic.webp',
    image: '/images/cat-slide-men-classic.webp',
    images: ['/images/cat-slide-men-classic.webp'],
    rating: 4.8,
    reviews: 30
  },
  {
    id: 'db-7',
    name: 'Relaxed Linen Drop Shirt',
    price: 1799,
    category: 'Shirts',
    gender: 'Womens',
    stock: 20,
    description: 'Breathable linen-cotton blended shirt with tailored cuffs and loose drape.',
    tag: 'Essential',
    sizes: ['S', 'M', 'L'],
    colors: ['Sage Green', 'White'],
    imageUrl: '/images/cat-slide-women-classic.webp',
    image: '/images/cat-slide-women-classic.webp',
    images: ['/images/cat-slide-women-classic.webp'],
    rating: 4.6,
    reviews: 19
  }
];

const DEFAULT_SPORTS_ITEMS = [
  {
    id: 'db-9',
    name: 'Pro Elite Breathable Sports Jersey',
    price: 1499,
    category: 'Sports Jerseys',
    gender: 'Sports',
    stock: 25,
    description: 'Engineered activewear sports jersey with micro-mesh airflow panels and ergonomic seams.',
    tag: 'New Drop',
    sizes: ['S', 'M', 'L', 'XL'],
    colors: ['Black', 'Neon Green', 'White'],
    imageUrl: '/images/cat-slide-sports-jersey.webp',
    image: '/images/cat-slide-sports-jersey.webp',
    images: ['/images/cat-slide-sports-jersey.webp'],
    rating: 4.9,
    reviews: 45
  },
  {
    id: 'db-10',
    name: 'Aeroready Dry-Fit Sports T-Shirt',
    price: 999,
    category: 'Sports T-Shirts',
    gender: 'Sports',
    stock: 35,
    description: 'High-density moisture-wicking athletic performance sports t-shirt engineered for workouts and movement.',
    tag: 'Performance',
    sizes: ['S', 'M', 'L', 'XL'],
    colors: ['Black', 'Royal Blue', 'Gray'],
    imageUrl: '/images/prod-sports-tee.webp',
    image: '/images/prod-sports-tee.webp',
    images: ['/images/prod-sports-tee.webp'],
    rating: 4.8,
    reviews: 32
  },
  {
    id: 'db-11',
    name: 'Velocity Micro-Mesh Training Jersey',
    price: 1399,
    category: 'Sports Jerseys',
    gender: 'Sports',
    stock: 20,
    description: 'Pro grade breathable micro-mesh training jersey with anti-chafing flatlock stitching.',
    tag: 'Unisex Drop',
    sizes: ['S', 'M', 'L', 'XL'],
    colors: ['Royal Blue', 'Black', 'Red'],
    imageUrl: '/images/cat-slide-sports-jersey.webp',
    image: '/images/cat-slide-sports-jersey.webp',
    images: ['/images/cat-slide-sports-jersey.webp'],
    rating: 4.9,
    reviews: 38
  },
  {
    id: 'db-12',
    name: 'Air-Flow Activewear Sports Tee',
    price: 899,
    category: 'Sports T-Shirts',
    gender: 'Sports',
    stock: 30,
    description: 'Ultra-lightweight moisture-control workout t-shirt designed for maximum athletic performance.',
    tag: 'Quick Dry',
    sizes: ['S', 'M', 'L', 'XL'],
    colors: ['Navy', 'Charcoal', 'White'],
    imageUrl: '/images/prod-sports-tee.webp',
    image: '/images/prod-sports-tee.webp',
    images: ['/images/prod-sports-tee.webp'],
    rating: 4.8,
    reviews: 29
  }
];

const Home = () => {
  const { products, setView, setFilters } = useContext(ShopContext);

  // Strictly segregated curated rows: T-Shirts & Polos (2 Boys + 2 Girls), Shirts, Sports
  const { teesAndPolosRow, shirtsRow, sportsRow } = useMemo(() => {
    const list = Array.isArray(products) && products.length > 0 ? products : [];

    // Helper to identify girls/women products
    const isGirl = (p) => {
      if (!p) return false;
      const g = (p.gender || '').toLowerCase();
      const n = (p.name || '').toLowerCase();
      const c = (p.category || '').toLowerCase();
      return g.includes('women') || g.includes('girl') || g.includes('female') ||
             n.includes('women') || n.includes('girl') || c.includes('women');
    };

    // Helper to identify boys/men products
    const isBoy = (p) => {
      if (!p) return false;
      if (isGirl(p)) return false;
      const g = (p.gender || '').toLowerCase();
      const n = (p.name || '').toLowerCase();
      const c = (p.category || '').toLowerCase();
      return g.includes('men') || g.includes('boy') || g.includes('male') ||
             n.includes('men') || n.includes('boy') || c.includes('men') || !g || g === 'all';
    };

    // 1. T-Shirts & Polos: Exactly 2 Boys products and 2 Girls products
    const girlTeesMatched = list.filter(p => p && isGirl(p));
    const boyTeesMatched = list.filter(p => p && isBoy(p));

    const boysRow = [...boyTeesMatched];
    for (const item of DEFAULT_BOYS_TEES_POLOS) {
      if (boysRow.length >= 2) break;
      if (!boysRow.some(existing => existing.id === item.id || existing.name.toLowerCase() === item.name.toLowerCase())) {
        boysRow.push(item);
      }
    }

    const girlsRow = [...girlTeesMatched];
    for (const item of DEFAULT_GIRLS_TEES_POLOS) {
      if (girlsRow.length >= 2) break;
      if (!girlsRow.some(existing => existing.id === item.id || existing.name.toLowerCase() === item.name.toLowerCase())) {
        girlsRow.push(item);
      }
    }

    const teesPolos = [
      boysRow[0] || DEFAULT_BOYS_TEES_POLOS[0],
      boysRow[1] || DEFAULT_BOYS_TEES_POLOS[1],
      girlsRow[0] || DEFAULT_GIRLS_TEES_POLOS[0],
      girlsRow[1] || DEFAULT_GIRLS_TEES_POLOS[1]
    ].filter(Boolean);

    // 2. Shirts Row
    const buildRow = (matcher, defaultItems) => {
      const matched = list.filter(p => p && matcher(p));
      const row = [...matched];
      for (const item of defaultItems) {
        if (row.length >= 4) break;
        if (!row.some(existing => existing.id === item.id || existing.name.toLowerCase() === item.name.toLowerCase())) {
          row.push(item);
        }
      }
      return row.slice(0, 4);
    };

    const shirts = buildRow(
      (p) => {
        const cat = (p.category || '').toLowerCase();
        const name = (p.name || '').toLowerCase();
        const isShirtOrTee = cat.includes('shirt') || cat.includes('oversize') || name.includes('shirt') || name.includes('tee') || name.includes('t-shirt');
        const notOther = !cat.includes('polo') && !cat.includes('hoodie') && !cat.includes('jersey');
        return isShirtOrTee && notOther;
      },
      DEFAULT_SHIRTS
    );

    // 3. Sports Row
    const sports = buildRow(
      (p) => {
        const cat = (p.category || '').toLowerCase();
        const gen = (p.gender || '').toLowerCase();
        const tag = (p.tag || '').toLowerCase();
        const name = (p.name || '').toLowerCase();
        const desc = (p.description || '').toLowerCase();
        return gen.includes('sport') || gen.includes('unisex') || 
               cat.includes('sport') || cat.includes('jersey') || 
               tag.includes('performance') || tag.includes('sports') ||
               name.includes('jersey') || name.includes('sports') ||
               desc.includes('sports') || desc.includes('athletic') || desc.includes('jersey');
      },
      DEFAULT_SPORTS_ITEMS
    );

    return {
      teesAndPolosRow: teesPolos,
      shirtsRow: shirts,
      sportsRow: sports
    };
  }, [products]);

  const openCategory = (gender, subCategory) => {
    setFilters(prev => ({
      ...prev,
      category: 'All',
      gender: gender || 'All',
      subCategory: subCategory || 'All',
      search: '',
      imageSearchPreview: null,
      imageSearchName: ''
    }));
    setView('shop');
  };

  const openAll = () => openCategory('All', 'All');
  const openTeesAndPolos = () => openCategory('All', 'Polos');
  const openShirts = () => openCategory('All', 'Shirts');
  const openSports = () => openCategory('Sports', 'All');

  return (
    <div className="homepage-wrapper">
      <Hero />
      <ValueProps />
      
      {/* 5 Video Clips Automated Live Reel Section */}
      <VideoShowcase />

      <section className="home-featured-section container reveal-el">
        {/* Animated Background Grid Overlay */}
        <div className="streetwear-grid-bg" />
        
        <div className="home-section-header">
          <div className="header-titles">
            <p className="overline">Just landed</p>
            <h2 className="section-title dynamic-heading">
              <TShirtIcon className="heading-tshirt-logo jumping-letter" style={{ '--char-index': 0 }} size={28} />
              <JumpingText text="The new essentials." />
            </h2>
            <p className="section-subtitle">Clean lines. Better fabric. The pieces that make getting dressed simple.</p>
          </div>
          <button onClick={openAll} className="view-all-link-btn">Shop the full drop <span>→</span></button>
        </div>
        
        {/* ============================================================ */}
        {/* 1. T-SHIRTS & POLOS SECTION (Shows 2 Boys + 2 Girls Products) */}
        {/* ============================================================ */}
        <div className="home-curated-block">
          <div className="curated-row-header">
            <div className="curated-header-left">
              <span className="curated-pill">SIGNATURE APPAREL</span>
              <h3 className="curated-title">Signature T-Shirts & Polos</h3>
            </div>
            <button type="button" onClick={openTeesAndPolos} className="curated-view-link">
              Explore All T-Shirts & Polos <span>→</span>
            </button>
          </div>
          <div className="grid-catalog">
            {teesAndPolosRow.map(product => (
              <ProductCard key={product.id} product={product} />
            ))}
          </div>
        </div>

        {/* ============================================================ */}
        {/* 2. CLASSIC SHIRTS & ESSENTIAL TEES SECTION                  */}
        {/* ============================================================ */}
        <div className="home-curated-block mt-curated-block">
          <div className="curated-row-header">
            <div className="curated-header-left">
              <span className="curated-pill">STRUCTURED DRAPE</span>
              <h3 className="curated-title">Classic Shirts & Essential Tees</h3>
            </div>
            <button type="button" onClick={openShirts} className="curated-view-link">
              Explore All Shirts <span>→</span>
            </button>
          </div>
          <div className="grid-catalog">
            {shirtsRow.map(product => (
              <ProductCard key={product.id} product={product} />
            ))}
          </div>
        </div>

        {/* ============================================================ */}
        {/* 3. UNISEX SPORTS WEAR SECTION                               */}
        {/* ============================================================ */}
        <div className="home-curated-block mt-curated-block">
          <div className="curated-row-header">
            <div className="curated-header-left">
              <span className="curated-pill">PERFORMANCE</span>
              <h3 className="curated-title">Unisex Sports Wear Collection</h3>
            </div>
            <button type="button" onClick={openSports} className="curated-view-link">
              View Sports Section <span>→</span>
            </button>
          </div>
          <div className="grid-catalog">
            {sportsRow.map(product => (
              <ProductCard key={product.id} product={product} />
            ))}
          </div>
        </div>
      </section>

      {/* 3D Floating Product Showcase Banner */}
      <FloatingShopCollage />
    </div>
  );
};

export default Home;
